import React, {
  forwardRef,
  memo,
  useCallback,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";

interface ConfirmDialogProps {
  title: string;
  description: string;
  setResult?: (value: boolean) => void;
  destructive?: boolean;
}

// Define the type for the ref object
export interface ConfirmDialogRef {
  openDialog: () => void;
}

const ConfirmDialog = memo(
  forwardRef<ConfirmDialogRef, ConfirmDialogProps>(
    ({ title, description, setResult, destructive }, ref) => {
      const [open, setOpen] = useState(false);

      const handleCancel = useCallback(() => {
        setOpen(false);
        setResult?.(false);
      }, [setResult]);

      const handleAccept = useCallback(() => {
        setOpen(false);
        setResult?.(true);
      }, [setResult]);

      const openDialog = useCallback(() => {
        setOpen(true);
      }, []);

      useImperativeHandle(ref, () => ({
        openDialog,
      }));

      return (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{title}</DialogTitle>
              <DialogDescription>{description}</DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                onClick={handleCancel}
                variant="outline"
                className={
                  destructive
                    ? "border-destructive text-destructive hover:bg-destructive/10"
                    : ""
                }
              >
                Cancel
              </Button>
              <Button
                onClick={handleAccept}
                variant={destructive ? "destructive" : "default"}
                autoFocus
              >
                Confirm
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      );
    },
  ),
);

export default ConfirmDialog;

export const useConfirmDialog = (props: ConfirmDialogProps) => {
  const ref = React.useRef<ConfirmDialogRef>(null);
  const [resolveResult, setResolveResult] =
    useState<(value: boolean) => void>();

  const openDialog = useCallback(() => {
    return new Promise<boolean>((resolve) => {
      setResolveResult(() => resolve);
      ref.current?.openDialog();
    });
  }, []);

  const ConfirmDialogComponent = useMemo(() => {
    return (
      <ConfirmDialog
        {...props}
        ref={ref}
        setResult={(value) => {
          resolveResult?.(value);
        }}
      />
    );
  }, [props, resolveResult]);

  return {
    ConfirmDialog: ConfirmDialogComponent,
    openDialog,
  };
};
